import { useEffect, useState } from "react"

// The ONE owner of blob: preview URLs in the consumer walk. A retake replaces
// (and revokes) the previous preview for that shot; removing a photo or
// leaving the flow revokes everything. Reducers never touch object URLs.

export class PreviewRegistry {
  private urls = new Map<string, string>()

  // New preview for a shot; revokes whatever that shot showed before.
  set(key: string, blob: Blob): string {
    this.release(key)
    const url = URL.createObjectURL(blob)
    this.urls.set(key, url)
    return url
  }

  // The photo moved to another key (re-filed as an extra): keep its preview.
  rename(from: string, to: string) {
    const url = this.urls.get(from)
    if (!url) return
    this.release(to)
    this.urls.set(to, url)
    this.urls.delete(from)
  }

  release(key: string) {
    const url = this.urls.get(key)
    if (url) URL.revokeObjectURL(url)
    this.urls.delete(key)
  }

  releaseAll() {
    for (const url of this.urls.values()) URL.revokeObjectURL(url)
    this.urls.clear()
  }

  // Live blob previews (tests assert this returns to 0 after leaving).
  size(): number {
    return this.urls.size
  }
}

export function usePreviewRegistry(): PreviewRegistry {
  const [registry] = useState(() => new PreviewRegistry())
  useEffect(() => () => registry.releaseAll(), [registry])
  return registry
}
